import React, { useCallback, useEffect, useState } from "react";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/styles";
import { Skeleton } from "@mui/material"; // スケルトンコンポーネントをインポート

import { ColorButtons } from "../UIkit";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import {
  ORDER_CANCELLED,
  ORDER_CONFIRMED,
  ORDER_PLACED,
  ORDER_RECEIVED,
} from "../../constants/orderStatus";
import { getUserInfo } from "../../reducks/users/selectors";
import classModule from "./OrderedProducts.module.scss";
import { ConfirmSingleStatuChangeModal } from "../UIkit/modal/ConfirmSingleStatusChangeModal";
import { EditOrderDatesModal } from "../UIkit/modal/EditOrderDatesModal";
import OrderDatesSummary from "../order/OrderDatesSummary";
import {
  canUserEditDates,
  canUserTransition,
} from "../../functions/order/statusTransition";
import NoImage from "../../assets/img/src/no_image.png";

const useStyles = makeStyles((theme) => ({
  list: {
    background: "#fff",
    position: "relative",
    height: "auto",
    display: "block",
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      justifyContent: "space-between",
    },
  },
  colorList: {
    background: "#ffff00",
    position: "relative",
    height: "auto",
    display: "block",
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      justifyContent: "space-between",
    },
  },
  image: {
    objectFit: "cover",
    margin: "8px 16px 8px 0",
    height: 96,
    width: 96,
    opacity: 0, // 初期状態で非表示
    transition: "opacity .5s ease",
  },
  imageLoaded: {
    opacity: 1, // 画像が読み込まれた後に表示
  },
  text: {
    width: "100%",
  },
}));

// ステータス変更ボタンの表示可否は functions/order/statusTransition.js に一本化してある。
// (かつては一括ダイアログと単票でルールが食い違い、検収済から戻す手段が
//  「取消を経由する」しか無かったため、経由中に注文日が上書きされていた)
const STATUS_BUTTONS = [
  { status: ORDER_PLACED, label: "注文", color: "placed" },
  { status: ORDER_CONFIRMED, label: "発注", color: "delete" },
  { status: ORDER_RECEIVED, label: "検収", color: "received" },
  { status: ORDER_CANCELLED, label: "取消", color: "grey" },
];

const OrderedProducts = (props) => {
  const classes = useStyles();
  const userInfo = useSelector(getUserInfo);
  const dispatch = useDispatch();
  const { products, updateOrderStatus, orderUid, orderNumber } = props;
  const [isShowDialog, setIsShowDialog] = useState(false);
  const [targetOrder, setTargetOrder] = useState(null);
  const [targetStatus, setTargetStatus] = useState(null);
  const [dateEditTarget, setDateEditTarget] = useState(null);
  const canEditDates = canUserEditDates(userInfo);

  const goToProductDetail = useCallback(
    (id) => {
      dispatch(push("/product/" + id));
    },
    [dispatch]
  );
  const handleStatusChange = useCallback((status, product) => {
    setIsShowDialog(true);
    setTargetOrder(product);
    setTargetStatus(status);
  }, []);
  const handleCancel = useCallback(() => {
    setIsShowDialog(false);
    setTargetOrder(null);
    setTargetStatus(null);
  }, []);
  const handleExec = useCallback(
    (status, order, dateInput) => {
      handleCancel();
      updateOrderStatus(status, order, dateInput);
    },
    [handleCancel, updateOrderStatus]
  );

  // 同じカート(order_id)の他の明細。日付編集で「まとめて適用」するときに使う
  const siblingsOf = useCallback(
    (product) =>
      (products || []).filter(
        (item) =>
          item.id !== product.id && item.order_id === product.order_id
      ),
    [products]
  );

  const [isLoading, setIsLoading] = useState(true); // ローディング状態
  const [isImageLoaded, setIsImageLoaded] = useState(false); // 画像が読み込まれたかどうか
  const handleImageLoad = () => {
    setIsLoading(false); // 画像が読み込まれたらローディングを解除
    setIsImageLoaded(true); // 画像が読み込まれたらフワッと表示
  };
  useEffect(() => {
    // 表示対象が入れ替わったらダイアログを閉じる(古い注文に対して実行するのを防ぐ)
    setIsShowDialog(false);
    setTargetOrder(null);
    setTargetStatus(null);
    setDateEditTarget(null);
  }, [orderUid]);

  return (
    <List>
      {products.map((product, index) => (
        <React.Fragment key={index}>
          <ListItem
            className={
              (orderUid && orderUid === product.id) ||
              (orderNumber &&
                Number(orderNumber) === Number(product.order_number))
                ? classes.colorList
                : classes.list
            }
          >
            <div className={classModule.productInfo}>
              <ListItemAvatar>
                {isLoading && (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    width={96}
                    height={96}
                  />
                )}{" "}
                {/* スケルトンローダーを表示 */}
                <img
                  className={`${classes.image} ${
                    isImageLoaded ? classes.imageLoaded : ""
                  }`} // 画像が読み込まれたらクラスを追加
                  src={
                    product.images && product.images.length > 0
                      ? product.images[0].path
                      : NoImage
                  }
                  alt={"Ordered Product"}
                  onLoad={handleImageLoad} // 画像が完全に読み込まれたら呼び出される
                />
              </ListItemAvatar>

              <div className={classes.text}>
                <ListItemText secondary={"注文番号: " + product.order_number} />
                <ListItemText
                  primary={product.name}
                  secondary={"サイズ: " + product.size}
                />
                <ListItemText
                  primary={"￥" + Number(product.totalPrice).toLocaleString()}
                  secondary={
                    "単価:￥" +
                    Number(product.price).toLocaleString() +
                    " 数量:" +
                    product.quantity
                  }
                />
              </div>
            </div>
            <div className={classModule.statusInfo}>
              <p
                className={`${classModule.status} ${
                  product.status === ORDER_CANCELLED
                    ? classModule.canceled
                    : product.status === ORDER_PLACED
                    ? classModule.placed
                    : product.status === ORDER_CONFIRMED
                    ? classModule.ordered
                    : classModule.received
                }`}
              >
                {product.status}
              </p>
              <div className={classModule.dates}>
                <OrderDatesSummary order={product} layout="grid" />
              </div>
              <div className={classModule.buttons}>
                <ColorButtons
                  label={"商品詳細"}
                  color="primary"
                  width={116}
                  fontSize={"12px"}
                  onClick={() => goToProductDetail(product.product_id)}
                />

                {STATUS_BUTTONS.map(
                  ({ status, label, color }) =>
                    canUserTransition(userInfo, product, status) && (
                      <ColorButtons
                        key={status}
                        label={label}
                        color={color}
                        onClick={() => handleStatusChange(status, product)}
                      />
                    )
                )}
                {canEditDates && (
                  <ColorButtons
                    label={"日付編集"}
                    color="primary"
                    width={90}
                    fontSize={12}
                    onClick={() => setDateEditTarget(product)}
                  />
                )}
              </div>
            </div>
          </ListItem>
          <Divider />
        </React.Fragment>
      ))}
      {isShowDialog && targetOrder && targetStatus && (
        <ConfirmSingleStatuChangeModal
          order={targetOrder}
          targetStatus={targetStatus}
          canEditDates={canEditDates}
          onExec={handleExec}
          onCancel={handleCancel}
          setIsShow={setIsShowDialog}
        />
      )}
      {dateEditTarget && (
        <EditOrderDatesModal
          order={dateEditTarget}
          siblings={siblingsOf(dateEditTarget)}
          onClose={() => setDateEditTarget(null)}
        />
      )}
    </List>
  );
};

export default OrderedProducts;
